import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import { IPD_COLORS, IPD_SHAPE } from "../../ipd/utils";
import { TRIAGE } from "../constants";

/**
 * Triage category as its colour, so severity reads at a glance wherever the
 * patient appears (ER-TRI-003).
 */
export function TriageBadge({ category, size = "small" }) {
  const t = TRIAGE[category];

  if (!t) {
    return (
      <Chip
        label="Not triaged"
        size={size}
        variant="outlined"
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          borderStyle: "dashed",
          color: IPD_COLORS.textMuted,
          borderColor: IPD_COLORS.borderHover,
        }}
      />
    );
  }

  return (
    <Chip
      label={t.label}
      size={size}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: t.bg,
        color: t.text,
        border: `1px solid ${t.border}`,
      }}
    />
  );
}

/** The coloured spine down the left edge of a board row. */
export function TriageStripe({ category }) {
  const t = TRIAGE[category];
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        borderTopLeftRadius: `${IPD_SHAPE.radiusMd}px`,
        borderBottomLeftRadius: `${IPD_SHAPE.radiusMd}px`,
        bgcolor: t ? t.color : IPD_COLORS.border,
      }}
    />
  );
}

export function StageBadge({ stage }) {
  if (!stage) return null;
  return (
    <Chip
      label={stage.label}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        bgcolor: stage.bg,
        color: stage.text,
        border: `1px solid ${stage.border}`,
      }}
    />
  );
}

/**
 * A medico-legal case is marked wherever it appears, so staff know the visit
 * carries legal obligations (ER-MLC-004, ER-BRD-003).
 */
export function MlcBadge({ caseNumber, compact = false }) {
  return (
    <Chip
      icon={<GavelIcon sx={{ fontSize: 13 }} />}
      label={compact ? "MLC" : caseNumber || "MLC"}
      size="small"
      title={caseNumber ? `Medico-legal case ${caseNumber}` : "Medico-legal case"}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: IPD_COLORS.info.bg,
        color: IPD_COLORS.info.text,
        border: `1px solid ${IPD_COLORS.info.border}`,
        "& .MuiChip-icon": { color: IPD_COLORS.info.text, ml: 0.5 },
      }}
    />
  );
}

/** Shared empty / error placeholder, styled on the IPD card tokens. */
export function EmergencyPlaceholder({ icon: Icon, title, description, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 3,
        py: 7,
        border: `1px dashed ${IPD_COLORS.borderHover}`,
        borderRadius: `${IPD_SHAPE.radiusMd}px`,
        bgcolor: IPD_COLORS.cardBg,
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: IPD_COLORS.pageBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <Icon sx={{ color: IPD_COLORS.textMuted, fontSize: 22 }} />
        </Box>
      )}
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: IPD_COLORS.textPrimary }}>
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{ mt: 0.5, maxWidth: 460, fontSize: 13.5, color: IPD_COLORS.textSecondary }}
        >
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}
